// src/components/Sidebar.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { logout as logoutAction } from "../redux/slices/authSlice";
import SOURCE_MAP from "../config/sourceMap";
import { SCREEN, usePermissions } from "../utils/permissions";
import useWeightDisputeAttention from "../hooks/useWeightDisputeAttention";
import {
  Package,
  Plus,
  Upload,
  RotateCcw,
  ShoppingCart,
  Store,
  Tag,
  Search,
  FileText,
  Download,
  Wrench,
  BarChart3,
  IndianRupee,
  AlertCircle,
  Wallet,
  User,
  ChevronDown,
  ChevronRight,
  PackageX,
  ChevronLeft,
  Sparkles,
  Star,
  MessageSquare,
  SlidersHorizontal,
  FileEdit,
  Settings,
  Lock,
  Users,
  Scale,
  LogOut,
} from "lucide-react";

/**
 * When true, denied nav items are hidden entirely.
 * When false, denied items are shown greyed-out with a lock icon.
 */
const HIDE_DENIED = true;

/**
 * ---- "Glide" hover system ----
 * There are no per-row hover backgrounds anymore. One soft highlight element
 * lives behind the rows of the scrollable list and travels (translate + size
 * morph) to whichever row the cursor or keyboard focus is on, fading out in
 * place when the cursor leaves the list. Nothing pops into existence; the
 * highlight is a living element that moves. Rows themselves respond with
 * colour-deepening only.
 */
const GLIDE_EASE = "cubic-bezier(.4,0,.2,1)";
const GLIDE_TRANSITION = `transform 180ms ${GLIDE_EASE}, width 180ms ${GLIDE_EASE}, height 180ms ${GLIDE_EASE}, opacity 150ms ease`;

/** Touch screens don't hover — only engage the glide on true hover devices. */
const HOVER_CAPABLE_MQ =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(hover: hover)")
    : null;

export default function Sidebar({ isOpen, isCollapsed, onCollapse }) {
  const [acctOpen, setAcctOpen] = useState(false);
  const [source, setSource] = useState(() => localStorage.getItem("source") || "wayuvega");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Identity for the footer - same JWT decode pattern as the Header.
  const identity = useMemo(() => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return { name: "User", initial: "U" };
      const decoded = jwtDecode(token);
      const raw = decoded.username || decoded.sub || "User";
      const name = raw.charAt(0).toUpperCase() + raw.slice(1);
      return { name, initial: name.charAt(0).toUpperCase() };
    } catch {
      return { name: "User", initial: "U" };
    }
  }, []);

  // Mirrors Header's logout exactly so both signouts behave identically.
  const handleSignOut = () => {
    dispatch(logoutAction());
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("parentClientId");
      localStorage.removeItem("merchantId");
    } catch {}
    const s = source || localStorage.getItem("source") || "wayuvega";
    const route = (SOURCE_MAP[s] && SOURCE_MAP[s].loginRoute) || "/login";
    navigate(route, { replace: true });
  };

  // Glide hover highlight: refs + transient state (no re-renders on hover).
  const scrollRef = useRef(null);
  const glideRef = useRef(null);
  const glideStateRef = useRef({ row: null, raf: 0 });

  // State for collapsible sections - Orders Management expanded by default
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem("expandedSections");
    return saved ? JSON.parse(saved) : ["Analytics", "Orders Management"];
  });

  // State for starred items
  const [starredItems, setStarredItems] = useState(() => {
    const saved = localStorage.getItem("starredItems");
    return saved ? JSON.parse(saved) : [];
  });

  // Icon mapping for routes
  const iconMap = {
    "All Shipments": Package,
    "Create Forward Order": Plus,
    "Create Bulk Order": Upload,
    "Create Reverse Order": RotateCcw,
    "Shopify Orders": ShoppingCart,
    "Marketplace Self-Ship Orders": Store,
    "Generate Label": Tag,
    "Bulk Tracking": Search,
    "Bulk Labels Download": Download,
    "Bulk Invoice Download": FileText,
    "Tools": Wrench,
    "Claim": AlertCircle,
    "Reports": BarChart3,
    "COD Remittance Report": IndianRupee,
    "Unprocessed Orders": AlertCircle,
    "Account Summary": Wallet,
    "Wallet": Wallet,
    "Profile": User,
    "Pickup Manifest": FileText,
    "NDR": PackageX,  // Added NDR icon
    "Weight Disputes": Scale,
    "Support Tickets": MessageSquare,
    "Report Configuration": SlidersHorizontal,
    "Draft Orders": FileEdit,
    "Account Configuration": Settings,
    "Team Users": Users,
  };

  const routes = [
    {
      section: "Orders Management",
      items: [
        { path: "/all-shipments", name: "All Shipments", screen: SCREEN.ALL_SHIPMENTS },
        { path: "/draft-orders", name: "Draft Orders", screen: SCREEN.DRAFT_ORDERS },
        { path: "/create-forward-order", name: "Create Forward Order", screen: SCREEN.CREATE_FORWARD },
        { path: "/create-bulk-order", name: "Create Bulk Order", screen: SCREEN.CREATE_BULK },
        { path: "/create-reverse-order", name: "Create Reverse Order", screen: SCREEN.CREATE_REVERSE },
        { path: "/pickup-manifest", name: "Pickup Manifest", screen: SCREEN.PICKUP_MANIFEST },
        { path: "/ndr-management", name: "NDR", screen: SCREEN.NDR_MANAGEMENT },
        { path: "/weight-disputes", name: "Weight\u00A0Disputes", screen: SCREEN.WEIGHT_DISPUTES },
        { path: "/claim-tracker", name: "Claim", screen: SCREEN.CLAIM_TRACKER },
      ]
    },
    {
      section: "Marketplace",
      items: [
        { path: "/channel-order", name: "Shopify Orders", screen: SCREEN.SHOPIFY_ORDERS },
        { path: "/marketplace-self-ship", name: "Marketplace Self-Ship Orders", screen: SCREEN.MARKETPLACE_SELF_SHIP },
      ]
    },
    {
      section: "Operations",
      items: [
        { path: "/bulk-tracking", name: "Bulk Tracking", screen: SCREEN.BULK_TRACKING },
        { path: "/shipping-bulk-labels-download", name: "Bulk Labels Download", screen: SCREEN.BULK_LABELS_DOWNLOAD },
        { path: "/bulk-invoice-download", name: "Bulk Invoice Download", screen: SCREEN.BULK_INVOICE_DOWNLOAD },
        { path: "/tools", name: "Tools", screen: SCREEN.TOOLS },
        { path: "/search-unprocessed-orders", name: "Unprocessed Orders", screen: SCREEN.UNPROCESSED_ORDERS },
        { path: "/support/tickets", name: "Support Tickets", screen: SCREEN.SUPPORT_TICKETS },
      ]
    },
    {
      section: "Reports & Finance",
      items: [
        { path: "/reports", name: "Reports", screen: SCREEN.REPORTS },
        { path: "/cod-remittance-reports", name: "COD Remittance Report", screen: SCREEN.COD_REMITTANCE },
        { path: "/wallet", name: "Wallet", screen: SCREEN.WALLET },
      ]
    },
    {
      section: "Control Center",
      items: [
        { path: "/settings/configuration", name: "Account Configuration", screen: SCREEN.ACCOUNT_CONFIG },
        { path: "/settings/report-configuration", name: "Report Configuration", screen: SCREEN.REPORT_CONFIG },
        { path: "/settings/scheduled-email-reports", name: "Scheduled Email Reports", screen: SCREEN.SCHEDULED_REPORTS },
      ]
    },
    {
      section: "Team",
      items: [
        { path: "/team/users", name: "Team Users" },
      ]
    },
    {
      section: "Settings",
      items: [
        { path: "/profile", name: "Profile" }, // No screen key - always visible
      ]
    }
  ];

  // V2 Permission checking
  const { canScreen: checkScreen, isReady: permsReady } = usePermissions();

  /** Check if a route item is allowed by V2 permissions. */
  const canItem = useCallback(
    (item) => {
      if (!permsReady) return true; // While loading, show all
      if (!item.screen) return true; // No screen key = always visible
      return checkScreen(item.screen);
    },
    [permsReady, checkScreen]
  );

  // Weight-dispute attention count (FE stopgap until the backend notification channel).
  const wdAttention = useWeightDisputeAttention(permsReady && checkScreen(SCREEN.WEIGHT_DISPUTES));

  /** Filter routes based on permissions (when HIDE_DENIED is true). */
  const filteredRoutes = useMemo(() => {
    if (!HIDE_DENIED) return routes;
    return routes
      .map((section) => ({
        ...section,
        items: section.items.filter(canItem),
      }))
      .filter((section) => section.items.length > 0);
  }, [routes, canItem]);

  // Save expanded sections to localStorage
  useEffect(() => {
    localStorage.setItem("expandedSections", JSON.stringify(expandedSections));
  }, [expandedSections]);

  // Save starred items to localStorage
  useEffect(() => {
    localStorage.setItem("starredItems", JSON.stringify(starredItems));
  }, [starredItems]);

  // Reveal the active route on navigation: expand its section (and sub-menu group).
  useEffect(() => {
    const containing = routes.find((sec) =>
      sec.items.some(
        (r) => r.path === location.pathname || (r.subRoutes || []).some((s) => s.path === location.pathname)
      )
    );
    if (!containing) return;
    setExpandedSections((prev) =>
      prev.includes(containing.section) ? prev : [...prev, containing.section]
    );
    if (containing.items.some((r) => (r.subRoutes || []).some((s) => s.path === location.pathname))) {
      setAcctOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Active-trail helpers: the selected route lights its parent row and section label too.
  const isPathActive = (path) => location.pathname === path;
  const isRouteTreeActive = (route) =>
    isPathActive(route.path) || (route.subRoutes || []).some((s) => isPathActive(s.path));
  const isSectionActive = (section) => section.items.some(isRouteTreeActive);

  useEffect(() => {
    const handler = (e) => {
      if (!e) return;
      if (e.key === "source") {
        setSource(e.newValue || "wayuvega");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const brand = SOURCE_MAP[source] || SOURCE_MAP.wayuvega;

  // Toggle section expansion
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => {
      if (prev.includes(sectionName)) {
        return prev.filter(s => s !== sectionName);
      } else {
        return [...prev, sectionName];
      }
    });
  };

  // Toggle starred status
  const toggleStar = (path, e) => {
    e.preventDefault();
    e.stopPropagation();
    setStarredItems(prev => {
      if (prev.includes(path)) {
        return prev.filter(p => p !== path);
      } else {
        return [...prev, path];
      }
    });
  };

  // ---- Glide engine ----
  // The glide is absolutely positioned inside the scroll container, so it lives
  // in content coordinates (rect delta + scrollTop) and scrolls WITH the rows —
  // no desync when the list scrolls.

  /** Position the glide over a row, measured against the scroll container. */
  const placeGlideAtRow = (row) => {
    const container = scrollRef.current;
    const glide = glideRef.current;
    if (!container || !glide || !row) return;
    const rowRect = row.getBoundingClientRect();
    const contRect = container.getBoundingClientRect();
    const x = rowRect.left - contRect.left + container.scrollLeft;
    const y = rowRect.top - contRect.top + container.scrollTop;
    glide.style.transform = `translate(${x}px, ${y}px)`;
    glide.style.width = `${rowRect.width}px`;
    glide.style.height = `${rowRect.height}px`;
  };

  /**
   * Send the glide to a row. While visible it slides + morphs there; when
   * hidden it is positioned INSTANTLY (transitions suppressed and flushed) and
   * only fades in — it must never slide in from a stale position.
   */
  const moveGlideToRow = (row) => {
    const glide = glideRef.current;
    const state = glideStateRef.current;
    if (!glide || !row || state.row === row) return;
    state.row = row;
    window.cancelAnimationFrame(state.raf);

    const shown = parseFloat(window.getComputedStyle(glide).opacity || "0") > 0.02;
    if (shown) {
      glide.style.transition = GLIDE_TRANSITION;
      placeGlideAtRow(row);
      glide.style.opacity = "1";
    } else {
      glide.style.transition = "none";
      placeGlideAtRow(row);
      glide.style.opacity = "0";
      glide.getBoundingClientRect(); // flush the silent jump before re-enabling transitions
      state.raf = window.requestAnimationFrame(() => {
        glide.style.transition = GLIDE_TRANSITION;
        glide.style.opacity = "1"; // fade in only
      });
    }
  };

  /** Fade the glide out in place (it keeps its last position). */
  const hideGlide = () => {
    const glide = glideRef.current;
    const state = glideStateRef.current;
    if (!glide) return;
    window.cancelAnimationFrame(state.raf);
    state.row = null;
    glide.style.opacity = "0";
  };

  /** Delegated hover: any element with data-glide="row" attracts the highlight. */
  const handleGlideMouseOver = (e) => {
    if (HOVER_CAPABLE_MQ && !HOVER_CAPABLE_MQ.matches) return; // touch: no hover
    const target = e.target;
    if (!target || typeof target.closest !== "function") return;
    const row = target.closest('[data-glide="row"]');
    if (row) moveGlideToRow(row);
  };

  const handleGlideMouseLeave = () => hideGlide();

  /** Keyboard parity: focus-visible focus moves the glide too. */
  const handleGlideFocusIn = (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== "function") return;
    let keyboardIntent = true;
    try {
      keyboardIntent = target.matches(":focus-visible");
    } catch {
      // :focus-visible unsupported — treat any focus as keyboard-driven
    }
    if (!keyboardIntent) return;
    const row = target.closest('[data-glide="row"]');
    if (row) moveGlideToRow(row);
  };

  const handleGlideFocusOut = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) hideGlide();
  };

  // Keep the glide glued to its row across re-renders/layout shifts (starring,
  // section toggles, sub-menu open); fade it out if its row unmounted.
  useEffect(() => {
    const state = glideStateRef.current;
    const container = scrollRef.current;
    const glide = glideRef.current;
    if (!state.row || !container || !glide) return;
    if (!container.contains(state.row)) {
      state.row = null;
      glide.style.opacity = "0";
      return;
    }
    placeGlideAtRow(state.row);
  });

  // Cancel any pending glide frame on unmount.
  useEffect(() => {
    const state = glideStateRef.current;
    return () => window.cancelAnimationFrame(state.raf);
  }, []);

  // ---- "Quiet chrome, one accent" class system ----
  // Surface: clean rail with a hairline border, no gradients.
  const containerStyle = (() => {
    if (source === "jumbomail") {
      return "bg-[#F8F5F0] border-r border-[#07374a]/15";
    }
    return "bg-white border-r border-slate-200/70";
  })();

  const linkActiveClass = source === "jumbomail"
    ? "relative flex items-center gap-2.5 min-h-10 py-1.5 px-3 rounded-lg text-[13px] bg-[#07374a]/[0.07] text-[#07374a] font-semibold transition-colors duration-150"
    : "relative flex items-center gap-2.5 min-h-10 py-1.5 px-3 rounded-lg text-[13px] bg-[#3877FC]/10 text-[#2A59BD] font-semibold transition-colors duration-150";

  // Hover rows carry NO background of their own — the shared glide highlight
  // travels beneath them; rows only deepen their text/icon colour.
  const linkDefaultClass = source === "jumbomail"
    ? "relative flex items-center gap-2.5 min-h-10 py-1.5 px-3 rounded-lg text-[13px] text-[#07374a]/75 font-medium hover:text-[#07374a] transition-colors duration-150"
    : "relative flex items-center gap-2.5 min-h-10 py-1.5 px-3 rounded-lg text-[13px] text-slate-600 font-medium hover:text-slate-900 transition-colors duration-150";

  // Slim rounded left accent bar - the active-state signature.
  const accentBarClass = source === "jumbomail"
    ? "absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-full bg-[#07374a]"
    : "absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-full bg-[#3877FC]";

  const iconIdleClass = source === "jumbomail"
    ? "flex-shrink-0 text-[#07374a]/50 group-hover:text-[#07374a] transition-colors duration-150"
    : "flex-shrink-0 text-slate-400 group-hover:text-slate-600 transition-colors duration-150";

  const iconActiveClass = source === "jumbomail"
    ? "flex-shrink-0 text-[#07374a]"
    : "flex-shrink-0 text-[#3877FC]";

  const starIdleClass = source === "jumbomail"
    ? "text-[#07374a]/30 opacity-0 group-hover:opacity-100 hover:text-[#07374a]/60"
    : "text-slate-300 opacity-0 group-hover:opacity-100 hover:text-slate-500";

  const sublinkActiveClass = source === "jumbomail"
    ? "flex items-center gap-3 h-9 pl-11 pr-3 rounded-lg text-[13px] bg-[#07374a]/[0.07] text-[#07374a] font-semibold transition-colors duration-150"
    : "flex items-center gap-3 h-9 pl-11 pr-3 rounded-lg text-[13px] bg-[#3877FC]/10 text-[#2A59BD] font-semibold transition-colors duration-150";

  const sublinkDefaultClass = source === "jumbomail"
    ? "flex items-center gap-3 h-9 pl-11 pr-3 rounded-lg text-[13px] text-[#07374a]/70 hover:text-[#07374a] transition-colors duration-150"
    : "flex items-center gap-3 h-9 pl-11 pr-3 rounded-lg text-[13px] text-slate-500 hover:text-slate-700 transition-colors duration-150";

  const expandButtonClass = source === "jumbomail"
    ? "flex items-center justify-between gap-2.5 w-full min-h-10 py-1.5 px-3 rounded-lg text-[13px] text-[#07374a]/75 font-medium hover:text-[#07374a] transition-colors duration-150 cursor-pointer"
    : "flex items-center justify-between gap-2.5 w-full min-h-10 py-1.5 px-3 rounded-lg text-[13px] text-slate-600 font-medium hover:text-slate-900 transition-colors duration-150 cursor-pointer";

  // Parent row when one of its sub-items is the active route.
  const expandButtonActiveClass = source === "jumbomail"
    ? "flex items-center justify-between gap-2.5 w-full min-h-10 py-1.5 px-3 rounded-lg text-[13px] bg-[#07374a]/[0.07] text-[#07374a] font-semibold transition-colors duration-150 cursor-pointer"
    : "flex items-center justify-between gap-2.5 w-full min-h-10 py-1.5 px-3 rounded-lg text-[13px] bg-[#3877FC]/10 text-[#2A59BD] font-semibold transition-colors duration-150 cursor-pointer";

  const getIcon = (name) => {
    const IconComponent = iconMap[name] || iconMap[String(name).replace(/\u00A0/g, " ")];
    return IconComponent ? <IconComponent className="w-5 h-5 flex-shrink-0" strokeWidth={2} /> : null;
  };

  // Get starred routes for the starred section
  const getStarredRoutes = () => {
    const allRoutes = routes.flatMap(section => section.items);
    return allRoutes.filter(route => starredItems.includes(route.path));
  };

  // Render a single route item (with V2 permission checking)
  const renderRouteItem = (route, i, showStar = true) => {
    const allowed = canItem(route);
    const childActive = (route.subRoutes || []).some((s) => isPathActive(s.path));

    // Denied items: hidden entirely when HIDE_DENIED, else greyed-out with lock.
    // The null guard also covers denied routes that were previously starred.
    if (!allowed) {
      if (HIDE_DENIED) return null;
      return (
        <li key={`${route.path}-${i}`} className="relative">
          <div
            className="flex items-center gap-2.5 min-h-10 py-1.5 px-3 rounded-lg text-[13px] text-slate-300 cursor-not-allowed select-none"
            title="You don't have access to this feature"
            aria-disabled="true"
          >
            {getIcon(route.name) && (
              <span className="text-slate-300 opacity-50 flex-shrink-0">{getIcon(route.name)}</span>
            )}
            <span className={`flex-1 min-w-0 leading-snug transition-opacity duration-200 ${isCollapsed ? 'md:opacity-0 md:w-0 md:flex-none md:overflow-hidden md:whitespace-nowrap' : 'md:opacity-100'}`}>
              {route.name}
            </span>
            {!isCollapsed && (
              <Lock className="w-3.5 h-3.5 text-slate-300 opacity-50 flex-shrink-0" />
            )}
          </div>
        </li>
      );
    }

    return (
    <li key={`${route.path}-${i}`} className="relative group">
      {!route.subRoutes ? (
        <>
          <NavLink
            to={route.path}
            end
            data-glide="row"
            title={isCollapsed ? route.name : undefined}
            className={({ isActive }) =>
              `${isActive ? linkActiveClass : linkDefaultClass}${isCollapsed ? " md:justify-center md:gap-0 md:px-0 md:w-11 md:min-h-11 md:py-0 md:mx-auto" : ""}`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span aria-hidden="true" className={accentBarClass}></span>}
                <span className={isActive ? iconActiveClass : iconIdleClass}>
                  {getIcon(route.name)}
                </span>
                <span className={`flex-1 min-w-0 leading-snug transition-opacity duration-200 ${isCollapsed ? 'md:opacity-0 md:w-0 md:flex-none md:overflow-hidden md:whitespace-nowrap' : 'md:opacity-100'}`}>
                  {route.name}
                </span>
                {route.path === "/weight-disputes" && wdAttention > 0 && (
                  <>
                    {!isCollapsed && (
                      <span
                        className="flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-100 text-amber-800 ring-1 ring-amber-200 text-[10px] font-bold flex items-center justify-center tabular-nums"
                        title={`${wdAttention} dispute${wdAttention === 1 ? '' : 's'} need your response`}
                      >
                        {wdAttention > 99 ? "99+" : wdAttention}
                      </span>
                    )}
                    {isCollapsed && (
                      <span aria-hidden="true" className="hidden md:block absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white"></span>
                    )}
                  </>
                )}
                {!isCollapsed && showStar && (
                  <button
                    onClick={(e) => toggleStar(route.path, e)}
                    className={`p-1 rounded flex-shrink-0 transition duration-150 hover:scale-110 ${
                      starredItems.includes(route.path) ? 'text-amber-400 hover:text-amber-500' : starIdleClass
                    }`}
                  >
                    <Star
                      className="w-3.5 h-3.5"
                      fill={starredItems.includes(route.path) ? "currentColor" : "none"}
                      strokeWidth={2}
                    />
                  </button>
                )}
              </>
            )}
          </NavLink>

          {/* Tooltip for collapsed state - Desktop only */}
          {isCollapsed && (
            <div className="tooltip hidden md:group-hover:block absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50 px-2.5 py-1.5 bg-slate-900 text-slate-50 text-xs font-medium rounded-md shadow-lg whitespace-nowrap">
              {route.name}
              <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900"></div>
            </div>
          )}
        </>
      ) : (
        <div>
          <button
            onClick={() => !isCollapsed && setAcctOpen(!acctOpen)}
            data-glide="row"
            title={isCollapsed ? route.name : undefined}
            className={`${childActive ? expandButtonActiveClass : expandButtonClass} ${isCollapsed ? 'md:justify-center' : ''}`}
          >
            <div className={`flex items-center gap-3 min-w-0 ${isCollapsed ? "md:flex-none md:gap-0" : "flex-1"}`}>
              <span className={childActive ? iconActiveClass : iconIdleClass}>{getIcon(route.name)}</span>
              <span className={`flex-1 leading-snug transition-opacity duration-200 ${isCollapsed ? 'md:opacity-0 md:w-0 md:flex-none md:overflow-hidden md:whitespace-nowrap' : 'md:opacity-100'}`}>
                {route.name}
              </span>
            </div>
            {!isCollapsed && (
              acctOpen ? (
                <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200" strokeWidth={2} />
              ) : (
                <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200" strokeWidth={2} />
              )
            )}
          </button>

          {/* Tooltip for collapsed state - Desktop only */}
          {isCollapsed && (
            <div className="tooltip hidden md:group-hover:block absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50 px-2.5 py-1.5 bg-slate-900 text-slate-50 text-xs font-medium rounded-md shadow-lg whitespace-nowrap">
              {route.name}
              <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900"></div>
            </div>
          )}

          {acctOpen && !isCollapsed && (
            <ul className="mt-1.5 space-y-1 animate-slideDown">
              {route.subRoutes.map((sub, j) => (
                <li key={j}>
                  <NavLink
                    to={sub.path}
                    data-glide={sub.disabled ? undefined : "row"}
                    className={({ isActive }) =>
                      sub.disabled
                        ? "flex items-center gap-3 h-9 pl-11 pr-3 rounded-lg text-[13px] bg-slate-100 text-slate-400 cursor-not-allowed"
                        : (isActive ? sublinkActiveClass : sublinkDefaultClass)
                    }
                  >
                    {getIcon(sub.name)}
                    <span className="flex-1 min-w-0 truncate">{sub.name}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
  };

  return (
    <>
      <style>{`
        .sidebar-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: ${source === "jumbomail" ? "rgba(7, 55, 74, 0.28)" : "rgba(56, 119, 252, 0.35)"} transparent;
        }
        .sidebar-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .sidebar-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-scrollbar::-webkit-scrollbar-thumb {
          background: ${source === "jumbomail" ? "rgba(7, 55, 74, 0.28)" : "rgba(56, 119, 252, 0.35)"};
          border-radius: 999px;
        }
        .sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${source === "jumbomail" ? "rgba(7, 55, 74, 0.5)" : "rgba(42, 89, 189, 0.6)"};
        }
        .tooltip {
          pointer-events: none;
          white-space: nowrap;
        }
        @media (prefers-reduced-motion: reduce) {
          .sidebar-glide {
            /* Honour reduced motion: no travel/morph animation, keep the gentle fade. */
            transition: opacity 150ms ease !important;
          }
        }
      `}</style>

      <nav
        className={`
          ${containerStyle}
          ${isCollapsed ? 'w-20' : 'w-64'}
          fixed top-16 left-0
          h-[calc(100vh-4rem)] z-30
          transform transition-all duration-300 ease-in-out
          ${isOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}
          md:translate-x-0 md:shadow-none
          flex flex-col
        `}
      >
        {/* Desktop Collapse Toggle - Only visible on desktop */}
        <button
          onClick={onCollapse}
          className={`
            hidden md:flex absolute -right-3 top-6 z-40
            w-6 h-6 items-center justify-center
            bg-white rounded-full shadow-sm border border-slate-200
            hover:shadow-md transition-all duration-150
            ${source === "jumbomail" ? "text-[#07374a]/70 hover:text-[#07374a]" : "text-slate-500 hover:text-[#2A59BD]"}
          `}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          ) : (
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
          )}
        </button>

        <div
          ref={scrollRef}
          className="relative flex-1 min-h-0 overflow-y-auto sidebar-scrollbar px-3 py-4"
          onMouseOver={handleGlideMouseOver}
          onMouseLeave={handleGlideMouseLeave}
          onFocus={handleGlideFocusIn}
          onBlur={handleGlideFocusOut}
        >
          {/* The glide: one soft wash that lives behind all rows and travels
              between them. It scrolls with the content, morphs to each row's
              size (incl. collapsed 44px tiles) and only ever fades in/out. */}
          <div
            ref={glideRef}
            aria-hidden="true"
            className="sidebar-glide pointer-events-none absolute left-0 top-0 z-0 rounded-lg"
            style={{
              background: source === "jumbomail" ? "rgba(7,55,74,0.05)" : "rgba(56,119,252,0.08)",
              width: 0,
              height: 0,
              transform: "translate(0px, 0px)",
              opacity: 0,
              transition: GLIDE_TRANSITION,
            }}
          />
          <div className="relative z-[1] space-y-5">
            {/* Starred Section */}
            {starredItems.length > 0 && !isCollapsed && (
              <div>
                <div className="px-3 mb-2 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                  <h3 className={`text-[10.5px] font-bold uppercase tracking-[0.12em] ${
                    source === "jumbomail" ? "text-[#07374a]/50" : "text-slate-400"
                  }`}>
                    Starred
                  </h3>
                </div>
                <ul className="space-y-1">
                  {getStarredRoutes().map((route, i) => renderRouteItem(route, i, true))}
                </ul>
              </div>
            )}

            {/* Regular Sections (permission-filtered) */}
            {filteredRoutes.map((section, sectionIdx) => {
              const isExpanded = expandedSections.includes(section.section);
              const sectionActive = isSectionActive(section);

              return (
                <div key={sectionIdx}>
                  {/* Section Header - Clickable to expand/collapse */}
                  {!isCollapsed ? (
                    <button
                      onClick={() => toggleSection(section.section)}
                      data-glide="row"
                      className="w-full px-3 py-1.5 mb-2 flex items-center justify-between group cursor-pointer rounded-lg"
                    >
                      <h3 className={`text-[10.5px] font-bold uppercase tracking-[0.12em] transition-colors duration-150 ${
                        source === "jumbomail"
                          ? (sectionActive ? "text-[#07374a]" : "text-[#07374a]/50 group-hover:text-[#07374a]/80")
                          : (sectionActive ? "text-[#2A59BD]" : "text-slate-400 group-hover:text-slate-600")
                      }`}>
                        {section.section}
                      </h3>
                      <div className={`transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}>
                        <ChevronDown className={`w-3.5 h-3.5 transition-colors duration-150 ${
                          source === "jumbomail"
                            ? (sectionActive ? "text-[#07374a]/80" : "text-[#07374a]/40 group-hover:text-[#07374a]/70")
                            : (sectionActive ? "text-[#3877FC]" : "text-slate-400 group-hover:text-slate-600")
                        }`} strokeWidth={2} />
                      </div>
                    </button>
                  ) : (
                    // Collapsed rail: groups separated by space, not lines
                    sectionIdx > 0 && (
                      <div className="h-2" aria-hidden="true"></div>
                    )
                  )}

                  {/* Section Items - Only show if expanded or sidebar is collapsed */}
                  {(isExpanded || isCollapsed) && (
                    <ul className="space-y-1">
                      {section.items.map((route, i) => renderRouteItem(route, i, true))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Identity footer - one slim line: avatar, name, sign out. Nothing else. */}
        <div className={`shrink-0 border-t px-3 pt-2.5 pb-2.5 flex flex-col gap-1.5 ${
          source === "jumbomail" ? "border-[#07374a]/10 bg-[#07374a]/[0.03]" : "border-slate-200/70 bg-slate-50/60"
        }`}>
        <div className={`flex items-center gap-2.5 min-h-[40px] ${isCollapsed ? "md:justify-center md:px-0" : ""}`}>
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{ background: brand.avatarGradient || "linear-gradient(135deg, #6F9FFD 0%, #3877FC 48%, #1C3B7E 100%)" }}
            title={identity.name}
          >
            {identity.initial}
          </div>
          <span className={`flex-1 min-w-0 truncate text-[13px] font-semibold ${
            source === "jumbomail" ? "text-[#07374a]" : "text-slate-800"
          } ${isCollapsed ? "md:hidden" : ""}`}>
            {identity.name}
          </span>
          <button
            type="button"
            onClick={handleSignOut}
            title="Sign out"
            aria-label="Sign out"
            className={`p-1.5 rounded-md text-slate-400 hover:text-rose-600 transition-colors flex-shrink-0 ${isCollapsed ? "md:hidden" : ""}`}
          >
            <LogOut className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
        <p className={`text-[10px] text-center tracking-wide ${isCollapsed ? "md:hidden" : ""} ${
          source === "jumbomail" ? "text-[#07374a]/55" : "text-slate-500"
        }`}>
          © {new Date().getFullYear()} {brand.title}
        </p>
        </div>
      </nav>
    </>
  );
}
